import {LocalizedText} from "crqlar-react-common/dist/models/common/localizedText.model";
import {GuestCategory} from "crqlar-react-common/dist/models/profile/guestCategory.model";
import {SurveyQuestionType} from "crqlar-react-common/dist/models/survey/survey.model";
import {
    SurveyConditionOperator,
    SurveyConditionSubject,
    SurveyQuestion,
    SurveyQuestionVisibilityRule
} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import {surveyMarkdownToPlainText} from "./surveyMarkdownText.util";
import {isScreen} from "./surveyQuestions.util";
import {getSurveyQuestionTypeFacts, SurveyAnswerKind} from "./surveyQuestionTypeFacts";
import {getSurveyQuestionTypeLabelKey} from "./surveyQuestionTypes.util";

export type VisibilitySourceKind = 'number' | 'boolean' | 'choice' | 'guestCategory';

export type VisibilityGuestCategory = Pick<GuestCategory, 'id' | 'name'>;

/** `sourceOff`: the condition stays, ready for when its question is switched on, but guests never answer that one. */
export type VisibilityRuleIssue = 'sourceMissing' | 'sourceOff' | 'sourceAfter';

export type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/** What a condition can test an answer as; a screen, a text, a date or a file is never a source. */
const SOURCE_KIND_BY_ANSWER: Record<SurveyAnswerKind, VisibilitySourceKind | undefined> = {
    none: undefined,
    number: 'number',
    boolean: 'boolean',
    options: 'choice',
    text: undefined,
    date: undefined,
    media: undefined,
};

const OPERATORS_BY_KIND: Record<VisibilitySourceKind, SurveyConditionOperator[]> = {
    number: [
        SurveyConditionOperator.GTE,
        SurveyConditionOperator.LTE,
        SurveyConditionOperator.EQUALS,
        SurveyConditionOperator.NOT_EQUALS,
    ],
    boolean: [
        SurveyConditionOperator.EQUALS,
    ],
    choice: [
        SurveyConditionOperator.INCLUDES_ANY,
        SurveyConditionOperator.EQUALS,
        SurveyConditionOperator.NOT_EQUALS,
    ],
    guestCategory: [
        SurveyConditionOperator.EQUALS,
        SurveyConditionOperator.NOT_EQUALS,
    ],
};

const OPERATOR_LABEL_KEYS: Record<VisibilitySourceKind, Partial<Record<SurveyConditionOperator, string>>> = {
    number: {
        [SurveyConditionOperator.GTE]: 'is at least',
        [SurveyConditionOperator.LTE]: 'is at most',
        [SurveyConditionOperator.EQUALS]: 'is exactly',
        [SurveyConditionOperator.NOT_EQUALS]: 'is not',
    },
    boolean: {
        [SurveyConditionOperator.EQUALS]: 'is',
    },
    choice: {
        [SurveyConditionOperator.INCLUDES_ANY]: 'includes any of',
        [SurveyConditionOperator.EQUALS]: 'includes all of',
        [SurveyConditionOperator.NOT_EQUALS]: 'does not include',
    },
    guestCategory: {
        [SurveyConditionOperator.EQUALS]: 'is',
        [SurveyConditionOperator.NOT_EQUALS]: 'is not',
    },
};

const isSet = <T>(value: T | undefined | null): value is T => value !== undefined && value !== null;

const localizedText = (text: LocalizedText | undefined, language: string) => {
    if (!text) return '';
    return text[language] || text['en'] || Object.values(text)[0] || '';
};

const ruleSubject = (rule: SurveyQuestionVisibilityRule) => rule.subject ?? SurveyConditionSubject.ANSWER;

export const visibilitySourceKind = (type: SurveyQuestionType | undefined): VisibilitySourceKind | undefined => {
    const facts = getSurveyQuestionTypeFacts(type);
    return facts ? SOURCE_KIND_BY_ANSWER[facts.answer] : undefined;
};

export const visibilityConditionSources = (
    questions: SurveyQuestion[],
    targetQuestionId: string | undefined
): SurveyQuestion[] => {
    if (!targetQuestionId) return [];

    const targetIndex = questions.findIndex(question => question.id === targetQuestionId);
    if (targetIndex < 0) return [];

    return questions
        .slice(0, targetIndex)
        .filter(question => !!question.id && !!visibilitySourceKind(question.type));
};

export const visibilityOperators = (kind: VisibilitySourceKind): SurveyConditionOperator[] => (
    OPERATORS_BY_KIND[kind]
);

export const visibilityOperatorLabelKey = (
    operator: SurveyConditionOperator,
    kind: VisibilitySourceKind
): string => OPERATOR_LABEL_KEYS[kind][operator] ?? 'is';

export const visibilityRuleIssue = (
    rule: SurveyQuestionVisibilityRule,
    questions: SurveyQuestion[],
    targetQuestionId: string | undefined
): VisibilityRuleIssue | undefined => {
    if (ruleSubject(rule) === SurveyConditionSubject.GUEST_CATEGORY) return undefined;

    const sourceIndex = questions.findIndex(question => !!question.id && question.id === rule.questionId);
    if (sourceIndex < 0) return 'sourceMissing';
    if (!visibilitySourceKind(questions[sourceIndex].type)) return 'sourceMissing';
    if (questions[sourceIndex].switchedOff) return 'sourceOff';

    const targetIndex = targetQuestionId
        ? questions.findIndex(question => question.id === targetQuestionId)
        : -1;
    if (targetIndex >= 0 && sourceIndex >= targetIndex) return 'sourceAfter';

    return undefined;
};

export const isCompleteVisibilityRule = (
    rule: SurveyQuestionVisibilityRule,
    questions: SurveyQuestion[]
): boolean => {
    if (ruleSubject(rule) === SurveyConditionSubject.GUEST_CATEGORY) {
        return !!rule.operator && !!rule.guestCategoryId;
    }
    if (!rule.questionId || !rule.operator) return false;

    const source = questions.find(question => question.id === rule.questionId);

    switch (visibilitySourceKind(source?.type)) {
        case 'number':
            return isSet(rule.numberValue);
        case 'boolean':
            return isSet(rule.booleanValue);
        case 'choice':
            return !!rule.optionIds?.length;
        default:
            return false;
    }
};

const questionSourceLabel = (
    question: SurveyQuestion,
    questions: SurveyQuestion[],
    language: string,
    t: TranslateFn
) => {
    const title = surveyMarkdownToPlainText(localizedText(question.title, language)).trim()
        || t(getSurveyQuestionTypeLabelKey(question.type));
    const index = questions
        .filter(candidate => !isScreen(candidate))
        .findIndex(candidate => candidate.id === question.id);

    return index >= 0 ? `${index + 1}. ${title}` : title;
};

export const booleanValueLabel = (source: SurveyQuestion | undefined, value: boolean, t: TranslateFn) => {
    if (source?.type === SurveyQuestionType.LEGAL) return value ? t('Accepted') : t('Not accepted');
    return value ? t('Yes') : t('No');
};

const optionValueLabel = (
    source: SurveyQuestion | undefined,
    optionIds: string[],
    language: string,
    t: TranslateFn
) => optionIds.map(optionId => {
    const option = source?.options?.find(candidate => candidate.id === optionId);
    if (!option) return t('Removed option');
    return localizedText(option.label, language).trim() || t('Untitled option');
}).join(', ');

const guestCategoryValueLabel = (
    guestCategoryId: string | undefined,
    guestCategories: VisibilityGuestCategory[] | undefined,
    t: TranslateFn
) => {
    if (!guestCategoryId) return '';

    // Without the list we cannot tell a renamed category from a deleted one, so both read the same.
    const category = guestCategories?.find(candidate => candidate.id === guestCategoryId);
    return category?.name.trim() || t('Unavailable guest category');
};

export const describeVisibilityRule = (
    rule: SurveyQuestionVisibilityRule,
    questions: SurveyQuestion[],
    language: string,
    t: TranslateFn,
    guestCategories?: VisibilityGuestCategory[]
): string => {
    const isGuestCategory = ruleSubject(rule) === SurveyConditionSubject.GUEST_CATEGORY;
    const source = isGuestCategory
        ? undefined
        : questions.find(question => !!question.id && question.id === rule.questionId);
    const kind = isGuestCategory ? 'guestCategory' : visibilitySourceKind(source?.type);

    if (!isGuestCategory && (!source || !kind)) return t('Deleted question');

    const sourceLabel = isGuestCategory
        ? t('Guest category')
        : questionSourceLabel(source!, questions, language, t);

    const operatorLabel = rule.operator && kind
        ? t(visibilityOperatorLabelKey(rule.operator, kind))
        : '';

    let valueLabel = '';
    if (kind === 'guestCategory') {
        valueLabel = guestCategoryValueLabel(rule.guestCategoryId, guestCategories, t);
    } else if (kind === 'number') {
        valueLabel = isSet(rule.numberValue) ? String(rule.numberValue) : '';
    } else if (kind === 'boolean') {
        valueLabel = isSet(rule.booleanValue) ? booleanValueLabel(source, rule.booleanValue, t) : '';
    } else if (kind === 'choice') {
        valueLabel = optionValueLabel(source, rule.optionIds ?? [], language, t);
    }

    return [sourceLabel, operatorLabel, valueLabel].filter(Boolean).join(' ');
};
